import React from "react";
import {
    Card,
    CardContent,
    CircularProgress,
    Stack,
    Typography,
    Button,
    Box,
    Alert,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    SelectChangeEvent
} from "@mui/material";
import LinkIcon from "@mui/icons-material/Link";
import RefreshIcon from "@mui/icons-material/Refresh";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import DownloadIcon from "@mui/icons-material/Download";
import servicesProvider from "../../../services/ServicesProvider";
import { RistoranteCassaincloud } from "orser-model/RistoranteCassaincloud";
import { withRouter } from "../../../utility/withRouter";
import CassaincloudDialog from "./CassaincloudDialog";

interface Props { }

interface State {
    loading: boolean;
    cassaincloudInfo: RistoranteCassaincloud | null;
    dialogOpen: boolean;
    salesDialogOpen: boolean;
    testingConnection: boolean;
    refreshingToken: boolean;
    downloadingSales: boolean;
    connectionStatus: 'unknown' | 'connected' | 'error';
    selectedMonth: number;
    selectedYear: number;
}

class CassaincloudRegistrationInfoLayout extends React.Component<Props, State> {
    ristoranteId: string;

    constructor(props: Props) {
        super(props);

        // @ts-ignore
        this.ristoranteId = props.router.params.idRistorante;

        const currentDate = new Date();
        this.state = {
            loading: true,
            cassaincloudInfo: null,
            dialogOpen: false,
            salesDialogOpen: false,
            testingConnection: false,
            refreshingToken: false,
            downloadingSales: false,
            connectionStatus: 'unknown',
            selectedMonth: currentDate.getMonth(), // 0-11
            selectedYear: currentDate.getFullYear()
        };
    }

    componentDidMount() {
        this.loadCassaincloudInfo();
    }

    async loadCassaincloudInfo() {
        this.setState({ loading: true });

        try {
            const ristoranteCassaincloud = await servicesProvider
                .getCassaincloudService()
                .getRistoranteCassaInCloud(this.ristoranteId);
            this.setState({
                loading: false,
                cassaincloudInfo: ristoranteCassaincloud,
                connectionStatus: 'connected'
            });
        } catch (error) {
            console.log("Nessuna configurazione Cassa in Cloud trovata");
            this.setState({
                loading: false,
                cassaincloudInfo: null,
                connectionStatus: 'unknown'
            });
        }
    }

    handleOpenDialog = () => {
        this.setState({ dialogOpen: true });
    };

    handleCloseDialog = () => {
        this.setState({ dialogOpen: false });
    };

    handleDialogSuccess = () => {
        this.loadCassaincloudInfo();
    };

    handleOpenSalesDialog = () => {
        this.setState({ salesDialogOpen: true });
    };

    handleCloseSalesDialog = () => {
        this.setState({ salesDialogOpen: false });
    };

    handleRefreshToken = async () => {
        this.setState({ refreshingToken: true });

        try {
            await servicesProvider
                .getCassaincloudService()
                .refreshToken(this.ristoranteId);

            this.setState({ refreshingToken: false });
        } catch (error) {
            console.error("Refresh token fallito:", error);
            this.setState({ refreshingToken: false });
        }
    };

    handleMonthChange = (event: SelectChangeEvent<number>) => {
        this.setState({ selectedMonth: event.target.value as number });
    };

    handleYearChange = (event: SelectChangeEvent<number>) => {
        this.setState({ selectedYear: event.target.value as number });
    };

    // Calcola le date per il download delle vendite
    getSalesDateRange = (month: number, year: number) => {
        // Primo giorno del mese selezionato
        const firstDayOfMonth = new Date(year, month, 1);

        // Start date: primo giorno del mese selezionato - 1 giorno
        const startDate = new Date(firstDayOfMonth);
        startDate.setDate(startDate.getDate() - 1);

        // End date: primo giorno del mese successivo
        const endDate = new Date(year, month + 1, 1);

        const formatDate = (date: Date) => {
            return date.toISOString().split('T')[0];
        };

        return {
            startDate: formatDate(startDate),
            endDate: formatDate(endDate)
        };
    };

    handleDownloadSales = async () => {
        this.setState({ downloadingSales: true });

        try {
            const { startDate, endDate } = this.getSalesDateRange(this.state.selectedMonth, this.state.selectedYear);

            await servicesProvider
                .getCassaincloudService()
                .processAndInsertSalesByDateRange(this.ristoranteId, startDate, endDate);

            // Chiudi il dialog dopo il successo
            this.setState({ salesDialogOpen: false });
        } catch (error) {
            console.error("Errore durante il download delle vendite da Cassa in Cloud:", error);
        } finally {
            this.setState({ downloadingSales: false });
        }
    };

    // Genera gli anni disponibili (ultimi 5 anni e prossimi 2)
    getAvailableYears = () => {
        const currentYear = new Date().getFullYear();
        const years = [];
        for (let year = currentYear - 5; year <= currentYear + 2; year++) {
            years.push(year);
        }
        return years;
    };

    // Nomi dei mesi
    getMonthNames = () => {
        return [
            'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
            'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'
        ];
    };

    getConnectionStatusChip = () => {
        const { connectionStatus, testingConnection } = this.state;

        if (testingConnection) {
            return (
                <Chip
                    label="Verificando..."
                    color="default"
                    size="small"
                    icon={<CircularProgress size={16} />}
                />
            );
        }

        switch (connectionStatus) {
            case 'connected':
                return (
                    <Chip
                        label="Connesso"
                        color="success"
                        size="small"
                    />
                );
            case 'error':
                return (
                    <Chip
                        label="Errore connessione"
                        color="error"
                        size="small"
                    />
                );
            default:
                return (
                    <Chip
                        label="Non verificato"
                        color="default"
                        size="small"
                    />
                );
        }
    };

    render() {
        const {
            loading,
            cassaincloudInfo,
            dialogOpen,
            salesDialogOpen,
            testingConnection,
            refreshingToken,
            downloadingSales,
            connectionStatus,
            selectedMonth,
            selectedYear
        } = this.state;

        return (
            <Stack width="100%" p={3}>
                <Stack direction={"column"} gap={1} p={3}>
                    <Typography variant="h6">
                        Integrazione Cassa in Cloud
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Gestisci la connessione tra il tuo ristorante e Cassa in Cloud per sincronizzare i dati di vendita.
                    </Typography>
                </Stack>

                {loading && (
                    <Box display="flex" justifyContent="center" p={4}>
                        <CircularProgress />
                    </Box>
                )}

                {!loading && !cassaincloudInfo && (
                    <Card>
                        <CardContent>
                            <Stack spacing={3} alignItems="center" p={2}>
                                <Typography variant="h6" align="center">
                                    Non hai ancora collegato il tuo account a Cassa in Cloud
                                </Typography>
                                <Alert severity="info" sx={{ width: "100%" }}>
                                    Collega il tuo ristorante a Cassa in Cloud per sincronizzare automaticamente i dati di vendita e gestire l'integrazione POS.
                                </Alert>
                                <Box>
                                    <Button
                                        variant="contained"
                                        startIcon={<LinkIcon />}
                                        onClick={this.handleOpenDialog}
                                    >
                                        Collega a Cassa in Cloud
                                    </Button>
                                </Box>
                            </Stack>
                        </CardContent>
                    </Card>
                )}

                {!loading && cassaincloudInfo && (
                    <Card>
                        <CardContent sx={{ p: 3 }}>
                            <Stack gap={3}>
                                <Stack direction="row" alignItems="center" gap={2}>
                                    <Typography variant="h6">
                                        Account collegato
                                    </Typography>
                                    {this.getConnectionStatusChip()}
                                </Stack>

                                <Stack gap={2}>
                                    <TextField
                                        value={cassaincloudInfo.apiKey || ''}
                                        label="Stato Autenticazione"
                                        disabled={true}
                                        fullWidth
                                        helperText="L'API Key è configurata e i token vengono gestiti automaticamente"
                                    />
                                </Stack>

                                <Alert severity="info">
                                    L'integrazione con Cassa in Cloud è attiva. I token di accesso vengono rinnovati automaticamente
                                    quando necessario. Usa i pulsanti sottostanti per verificare la connessione, forzare un aggiornamento o scaricare le vendite.
                                </Alert>

                                {connectionStatus === 'error' && (
                                    <Alert severity="warning">
                                        La verifica della connessione ha rilevato un problema.
                                        Prova a aggiornare il token o contatta il supporto se il problema persiste.
                                    </Alert>
                                )}

                                <Box display="flex" justifyContent="flex-end" gap={2}>
                                    <Button
                                        onClick={this.handleOpenSalesDialog}
                                        variant="outlined"
                                        startIcon={<SyncAltIcon />}
                                        disabled={downloadingSales || refreshingToken || testingConnection}
                                    >
                                        Aggiorna vendite
                                    </Button>
                                </Box>
                            </Stack>
                        </CardContent>
                    </Card>
                )}

                {/* Dialog per la configurazione iniziale */}
                <Dialog open={dialogOpen} onClose={this.handleCloseDialog} maxWidth="sm" fullWidth>
                    <CassaincloudDialog
                        ristoranteId={this.ristoranteId}
                        open={dialogOpen}
                        onClose={this.handleCloseDialog}
                        onSuccess={this.handleDialogSuccess}
                    />
                </Dialog>

                {/* Dialog per il download delle vendite */}
                <Dialog
                    open={salesDialogOpen}
                    onClose={this.handleCloseSalesDialog}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>
                        Seleziona periodo per aggiornare le vendite da Cassa in Cloud
                    </DialogTitle>
                    <DialogContent>
                        <Stack spacing={3} sx={{ mt: 2 }}>
                            <FormControl fullWidth>
                                <InputLabel>Anno</InputLabel>
                                <Select
                                    value={selectedYear}
                                    label="Anno"
                                    onChange={this.handleYearChange}
                                >
                                    {this.getAvailableYears().map((year) => (
                                        <MenuItem key={year} value={year}>
                                            {year}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl fullWidth>
                                <InputLabel>Mese</InputLabel>
                                <Select
                                    value={selectedMonth}
                                    label="Mese"
                                    onChange={this.handleMonthChange}
                                >
                                    {
                                        this.getMonthNames().map((month, index) => (
                                            <MenuItem key={index} value={index}>
                                                {month}
                                            </MenuItem>
                                        ))
                                    }
                                </Select>
                            </FormControl>

                            <Alert severity="info">
                                <Typography variant="body2">
                                    <strong>Periodo selezionato:</strong> {this.getMonthNames()[selectedMonth]} {selectedYear}
                                </Typography>
                                <Typography variant="body2">
                                    Le vendite verranno scaricate dal{' '}
                                    <strong>
                                        {(() => {
                                            const { startDate, endDate } = this.getSalesDateRange(selectedMonth, selectedYear);
                                            return `${startDate} al ${endDate}`;
                                        })()}
                                    </strong>
                                </Typography>
                            </Alert>

                            <Alert severity="warning">
                                <Typography variant="body2">
                                    <strong>Nota:</strong> Il sistema elaborerà automaticamente tutti i tipi di documento
                                    (fatture, ricevute, ordini, invoices) suddividendo il periodo in chunk di 2 giorni
                                    per rispettare i limiti dell'API di Cassa in Cloud.
                                </Typography>
                            </Alert>
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button variant="outlined" disabled={downloadingSales} onClick={this.handleCloseSalesDialog}>
                            Annulla
                        </Button>
                        <Button
                            onClick={this.handleDownloadSales}
                            variant="contained"
                            disabled={downloadingSales}
                            startIcon={downloadingSales ? <CircularProgress size={20} /> : <DownloadIcon />}
                        >
                            {downloadingSales ? 'Scaricando...' : 'Aggiorna vendite'}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Stack>
        );
    }
}

export default withRouter(CassaincloudRegistrationInfoLayout);